<template>
    <div class="page-task-history">
        <h3 class="title">{{ $t("automation.autoPlanAndTaskHistory") }}</h3>
        <div class="table-wrapper">
            <div class="toolbar">
                <div class="left">
                    <a-input-search
                        style="width: 300px"
                        v-model="searchText"
                        :placeholder="$t('message.startFilter')"
                        class="row search-input"
                        @input="onSearch($event.target.value)"
                    />
                    <CommonFilterTool
                        v-model="breakdownFilter"
                        :keep-state-on-query-params="false"
                        :filter-meta="filterMetas"
                        :label-formatter="filterLabelFormatter"
                        @addFilter="addFilter"
                    />
                </div>
                <div class="right"></div>
            </div>
            <SupsisTable
                :columns="getCols()"
                :rows="items"
                top-pagination
                empty-data-bordered
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    checkboxOption,
                    rowStyleOption,
                    clipboardOption,
                    fixedHeader: true,
                    id: 'automation-table',
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="(cursor) => (pagination = cursor)"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import CommonFilterTool from "../../../Chatbot/pages/archive/CommonFilterTool.vue";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import NoMessage from "../../../TeamChat/NoMessage.vue";
import log2 from "../../../../../utils/log2";
import HoverMenuCell from "../../../../../components/common/HoverMenuCell.vue";
import moment from "moment/moment";
import { AUTOMATION_API } from "../../../../../api/automation";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import Modal2 from "../../../../../components/common/Modal2.vue";
import EventSelector from "../../components/EventSelector.vue";
import WebhookAutomationForm from "../../components/WebhookAutomationForm.vue";

const log = log2("AutomationTaskHistory");
export default Vue.extend({
    name: "AutomationTaskHistory",
    components: {
        WebhookAutomationForm,
        EventSelector,
        Modal2,
        SupsisButton,
        NoMessage,
        SupsisTable,
        CommonFilterTool,
    },
    props: ["type", "defaultAutomationId"],
    data() {
        return {
            searchText: null,
            items: [],
            pagination: {
                page: 1,
                perPage: 20,
                total: 0,
                defaultPageSize: 50,
                hideOnSinglePage: true,
                pageSizeOptions: [10, 15, 20, 50, 100],
                showQuickJumper: true,
                showSizeChanger: true,
            },
            loading: true,
            loadingInstance: null,
            checkboxOption: {
                selectedRowKeys: [],
                // row select change event
                selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
                    console.log("selectedRowChange", { row, isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
                // selected all change event
                selectedAllChange: ({ isSelected, selectedRowKeys }) => {
                    console.log("selectedAllChange", { isSelected, selectedRowKeys });
                    this.checkboxOption.selectedRowKeys = selectedRowKeys;
                },
            },
            rowStyleOption: {
                stripe: true,
            },
            clipboardOption: {
                copy: true,
                paste: false,
                cut: false,
                delete: false,
            },
            filterMetas: [
                {
                    name: "status",
                    label: this.$t("automation.planStatus"),
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        {
                            id: "scheduled",
                            label: this.$t("automation.scheduled"),
                        },

                        {
                            id: "completed",
                            label: this.$t("automation.completed"),
                        },
                        {
                            id: "cancelled",
                            label: this.$t("automation.cancelled"),
                        },
                    ],
                },
                {
                    name: "automation",
                    label: this.$t("automation.automation"),
                    materialIcon: "filter_alt",
                    controlType: "select",
                    defaultValue: null,
                    options: () =>
                        this.$store.state.automation.automations.map((x) => ({
                            id: x._id,
                            label: x.name,
                        })),
                },
            ],
            breakdownFilter: [
                {
                    name: "status",
                    label: this.$t("automation.planStatus"),
                    filterValue: null,
                    materialIcon: "filter_alt",
                    controlType: "select",
                    options: () => [
                        {
                            id: "scheduled",
                            label: this.$t("automation.scheduled"),
                        },

                        {
                            id: "completed",
                            label: this.$t("automation.completed"),
                        },
                        {
                            id: "cancelled",
                            label: this.$t("automation.cancelled"),
                        },
                    ],
                },
                this.defaultAutomationId
                    ? {
                          name: "automation",
                          label: this.$t("automation.automation"),
                          filterValue: this.defaultAutomationId,
                          materialIcon: "filter_alt",
                          controlType: "select",
                          options: () =>
                              this.$store.state.automation.automations.map((x) => ({
                                  id: x._id,
                                  label: x.name,
                              })),
                      }
                    : undefined,
            ].filter((x) => !!x),
        };
    },
    methods: {
        fetch() {
            const cursor = {
                offset: (this.pagination.page - 1) * this.pagination.perPage,
                limit: this.pagination.perPage,
            };
            const filters = [
                (this.searchText || "").trim().length > 0
                    ? {
                          key: "*",
                          value: (this.searchText || "").trim(),
                      }
                    : undefined,
                this.type
                    ? {
                          key: "options.trigger",
                          value: this.type,
                          operator: "equals",
                      }
                    : undefined,

                ...this.generateFilterParams(),
            ].filter((x) => !!x);
            AUTOMATION_API.PLAN.list(filters, cursor)
                .then((response) => {
                    const { items, count } = response;
                    this.items = items;
                    this.pagination.total = count;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.$message.error("Automation Plans could not be fetched");
                    console.error("Error while fetching automations", error);
                });
        },
        getCols() {
            return [
                {
                    field: "",
                    key: "a",
                    type: "checkbox",
                    title: "",
                    width: 50,
                    align: "center",
                },
                {
                    key: "name",
                    field: "name",
                    title: this.$t("automation.automation"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.automation?.name}</span>;
                    },
                },
                {
                    key: "status",
                    field: "status",
                    title: this.$t("message.status"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            HoverMenuCell,
                            {
                                props: {
                                    filterAdd: !this.isFilterExists("status"),
                                    filterRemove: this.isFilterExists("status"),
                                    copy: true,
                                },
                                on: {
                                    filterAdded: () => {
                                        this.addFilter("status", row.status);
                                    },
                                    filterRemoved: () => {
                                        this.removeFilter("status");
                                    },
                                },
                            },
                            [
                                h(
                                    "span",
                                    {
                                        class: ["status"],
                                        attrs: {
                                            "data-status": row.status,
                                        },
                                    },
                                    row.status
                                ),
                            ]
                        );
                    },
                },
                {
                    key: "task",
                    field: "task",
                    title: this.$t("automation.taskCreated"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                class: ["status"],
                                attrs: {
                                    "data-status": row.task ? "completed" : "scheduled",
                                },
                            },
                            row.task ? "Task Created" : "Waiting Task Creation..."
                        );
                    },
                },
                {
                    key: "task",
                    field: "task",
                    title: this.$t("automation.taskStatus"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return h(
                            "span",
                            {
                                class: ["status"],
                                attrs: {
                                    "data-status": row.task?.status,
                                },
                            },
                            row.task?.status
                        );
                    },
                },

                {
                    key: "terminateReason",
                    field: `terminateReason`,
                    title: this.$t("automation.terminateReason"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.task?.terminateReason}</span>;
                    },
                },
                {
                    key: "task.duration",
                    field: "task.duration",
                    title: this.$t("automation.taskDuration"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const diff = moment.duration(row.task?.state?.duration, "seconds");
                        let f = moment.utc(diff.asMilliseconds()).format("HH:mm:ss.SSS");
                        return <span>{f}</span>;
                    },
                },
                {
                    key: "wakeUpCount",
                    field: "wakeUpCount",
                    title: this.$t("automation.taskWakeUpCount"),
                    width: "",
                    align: "left",
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{row.task?.state?.wakeUpCount}</span>;
                    },
                },
                {
                    key: "createdAt",
                    field: `createdAt`,
                    title: this.$t("automation.createdAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.createdAt).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    key: "completedAt",
                    field: `completedAt`,
                    title: this.$t("automation.completedAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <span>{moment(row.completedAt).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    key: "cancelledAt",
                    field: `cancelledAt`,
                    title: this.$t("automation.cancelledAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isCancelled = row.status === "cancelled";
                        return <span>{isCancelled ? moment(row.cancelledAt).format("DD.MM.YYYY HH:mm") : "-"}</span>;
                    },
                },
                {
                    key: "runAt",
                    field: `runAt`,
                    title: this.$t("automation.nextRunAt"),
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        const isScheduled = row.status === "scheduled";
                        return <span>{isScheduled ? moment(row.runAt).format("DD.MM.YYYY HH:mm") : "-"}</span>;
                    },
                },
                {
                    title: this.$t("message.action"),
                    key: "operation",
                    width: "100px",
                    align: "center",
                    operationColumn: true,
                    renderBodyCell: ({ row, column, rowIndex }, h) => {
                        return <div class="actions"></div>;
                    },
                },
            ];
        },
        generateFilterParams() {
            return this.breakdownFilter
                .map((filter) => {
                    if (!!filter.filterValue) {
                        return {
                            key: filter.name,
                            value: filter.filterValue,
                            operator: "equals",
                        };
                    }
                    return undefined;
                })
                .filter((x) => !!x);
        },
        onSearch(value) {
            log("onSearch", value);
            this.searchText = value;
            this.fetch();
        },
        addFilter(name, value) {
            log.info("addFilter", name);
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.breakdownFilter.find((x) => x.name === name);
            if (filter) {
                this.breakdownFilter = this.breakdownFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.breakdownFilter.push({
                    name: meta.name,
                    label: meta.label,
                    filterValue: value || meta.defaultValue,
                    materialIcon: meta.materialIcon,
                    controlType: meta.controlType,
                    options: meta.options,
                });
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.breakdownFilter = this.breakdownFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });
            const isSelect = filter.controlType === "select";
            if (isSelect) {
                const option = filter.options().find((x) => x.id === value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                }
            }
            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        isFilterExists(name) {
            return !!this.breakdownFilter.find((x) => x.name === name);
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: document.querySelector(".table-wrapper"),
            name: "wave",
        });
        if (this.loading) this.loadingInstance.show();
        this.fetch();
    },
    watch: {
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
        channelId(val) {
            this.searchText = "";
            this.pagination.page = 1;
            this.fetch();
        },
        pagination: {
            handler(val, oldVal) {
                const pageSizeChanged = val.perPage !== oldVal.perPage;
                if (pageSizeChanged) {
                    this.pagination.page = 1;
                }
                console.log("pagination changed", val);
                this.fetch();
            },
            deep: true,
        },
        breakdownFilter: {
            handler(val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        type(val) {
            this.fetch();
        },
    },
});
</script>

<style scoped lang="scss">
.page-task-history {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 10px;
    background: var(--color-bg-default);
    max-height: calc(100vh - 52px);
    height: calc(100vh - 52px);
    overflow: scroll;

    .table-wrapper {
        display: flex;
        flex-direction: column;
        gap: 5px;

        .toolbar {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 10px 0;
            gap: 5px;

            .left {
                display: flex;
                gap: 5px;
                align-items: center;
            }

            .right {
                display: flex;
                gap: 5px;
            }

            .common-filter-tool {
                border-bottom: none !important;
                padding: unset !important;
            }
        }
    }
}
</style>
<style lang="scss">
.page-task-history {
    .ant-modal {
        width: 75vw !important;
    }

    .custom-modal-body {
        background: var(--color-bg-default) !important;
    }

    span.status {
        color: rgb(45, 46, 46);
        padding: 3px 8px;
        -webkit-box-align: center;
        align-items: center;
        -webkit-box-pack: center;
        justify-content: center;
        text-align: center;
        border-radius: 30px;
        font-size: 12px;
        line-height: 15px;
        font-weight: 600;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        width: max-content;

        &[data-status="completed"] {
            background: rgb(222, 243, 233);
        }

        &[data-status="scheduled"] {
            background: rgb(255, 244, 188);
        }

        &[data-status="cancelled"] {
            background: rgb(250, 217, 239);
        }

        &[data-status="failed"] {
            background: rgb(250, 217, 239);
        }

        &[data-status="running"] {
            background: rgb(222, 243, 233);
        }

        &[data-status="waiting-reply"] {
            background: rgb(255, 244, 188);
        }

        &[data-status="done"] {
            background: rgb(222, 243, 233);
        }
    }

    //.md-switch-container {
    //    background: rgb(34 39 45 / 33%) !important;
    //}
    //
    //.md-switch-thumb {
    //    background-color: #22272d !important;
    //}
}
</style>
